"use client";
import clsx from "clsx";
import React, { useMemo } from "react";

import Button, { ButtonStyle } from "components/Button";
import { PAGE_LIMIT } from "constants/pagination";
import { PageInfo, PaginationInput } from "graphql/type.ts";
import { calcTotalPage } from "utils/sum.util.ts";

const Pagination = ({
  pagination,
  setPaginationInput,
  currentPage,
  setCurrentPage,
}: {
  pagination?: PageInfo | null;
  setPaginationInput: (_: PaginationInput) => void;
  currentPage?: number;
  setCurrentPage: (_: number) => void;
}) => {
  // １ページで20つデータで全部のページ数を計算
  const totalPages = useMemo(() => {
    return calcTotalPage(pagination?.totalCount || 0, PAGE_LIMIT);
  }, [pagination?.totalCount]);

  const onPageChange = (page: number) => {
    if (currentPage && page === currentPage + 1) {
      setPaginationInput({
        after: pagination?.endCursor || undefined,
        first: PAGE_LIMIT, // PAGE_LIMIT = 20
      });
    } else if (currentPage && page === currentPage - 1) {
      setPaginationInput({
        before: pagination?.startCursor || undefined,
        last: PAGE_LIMIT, // PAGE_LIMIT = 20
      });
    }
    setCurrentPage(page);
  };

  const displayNumberArray = useMemo(() => {
    const pages: number[] = [];

    // 前のページ番号を追加、もし存在する場合
    if (currentPage && currentPage > 1) {
      pages.push(currentPage - 1);
    }

    // 今のページ番号を追加
    currentPage && pages.push(currentPage);

    // 次のページ番号を追加、もし存在する場合
    if (currentPage && currentPage < totalPages) {
      pages.push(currentPage + 1);
    }
    return pages;
  }, [currentPage, totalPages]);

  // 表示すべきものがない場合は、React.Fragmentを返す
  if (displayNumberArray.length <= 1) return <></>;

  return (
    <div className="flex justify-center">
      <div className="mr-2 w-[79px]">
        {currentPage && (
          <Button
            text={"< 前へ"}
            style={ButtonStyle.WHITE}
            isDisabled={currentPage === 1}
            height="h-10"
            minWidth="w-[79px]"
            onClick={() => onPageChange(currentPage - 1)}
          />
        )}
      </div>
      {displayNumberArray[0] !== 1 && <div className="px-2 py-2 text-link">···</div>}
      {
        // 表示すべきページ番号を表示する
        displayNumberArray.map((page) => (
          <div
            key={page}
            onClick={() => onPageChange(page)}
            className={clsx("cursor-pointer px-2 py-2 text-[16px]", {
              "text-disabled": currentPage === page,
              "text-link": currentPage !== page,
            })}
          >
            {page}
          </div>
        ))
      }
      {displayNumberArray[displayNumberArray.length - 1] !== totalPages && (
        <div className="px-2 py-2 text-link">···</div>
      )}

      <div className="ml-2 w-[79px]">
        {currentPage && (
          <Button
            text={"次へ >"}
            height="h-10"
            minWidth="w-[79px]"
            style={ButtonStyle.WHITE}
            isDisabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          />
        )}
      </div>
    </div>
  );
};

export default Pagination;
